/**
 * Экран «Статистика» · Ф2.4 — на ЖИВЫХ данных `GET /progress` (ядро `core/progress.py`).
 *
 * Четыре блока, и под каждым лежит уже посчитанная ядром цифра:
 *   1. две главные — «узнаю» (бокс 0–1) и «достаю» (бокс 2+);
 *   2. из чего словарь — части речи из `lexicon` (правда о ЯЗЫКЕ, не поле очереди),
 *      каждая полоса поделена на выучено / учу / впереди;
 *   3. лестница Nation 2006 в семьях слов — с источником у каждой ступени, отметкой
 *      «вы здесь» и потолком словаря;
 *   4. народный ориентир владельца — подписанный как ориентир, а не как норма.
 *
 * ⛔ Ни вердикта об уровне («B1»), ни «понимаешь X% речи» — запрет ядра, он же и здесь.
 * ⛔ Графика активности по дням НЕТ: эти цифры лежат в `reviews`, но наружу их не отдаёт
 *    ни одна read-only функция ядра, а рисовать полосы «на глаз» — врать.
 *
 * Раскладка (группировка частей речи, выбор ступени, доли полос) вынесена в `src/stats.ts`
 * и проверяется запуском: `cd Lang/web && node tools/check-screens.mjs`.
 */

import "../styles/stats.css";

import { state } from "../state";
import {
  familyMid, ladderView, posRows, scaleBars,
  type PosRow, type RungView, type ScaleBar,
} from "../stats";

const num = (n: number) => n.toLocaleString("ru-RU");

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Диапазон семей «28–29» одной подписью; если края сошлись — одна цифра. */
function famRange(r: [number, number]): string {
  return r[0] === r[1] ? num(r[0]) : `${num(r[0])}–${num(r[1])}`;
}

function posBlock(rows: PosRow[], total: number): string {
  const line = (r: PosRow) => {
    // Счётчик «выучено · учу» показываем только когда есть что показывать: у нового
    // словаря он был бы строкой нулей на каждой из шести полос.
    const touched = r.known + r.learning;
    const sub = touched > 0
      ? `<div class="ps">выучено ${num(r.known)} · учу ${num(r.learning)}</div>`
      : (r.hint ? `<div class="ps">${esc(r.hint)}</div>` : "");
    return (
      '<div class="posrow">' +
        `<div class="top"><span class="nm">${esc(r.label)}</span>` +
        `<span class="ct">${num(r.total)}</span></div>` +
        '<div class="track">' +
          `<i class="known" style="width:${r.pctKnown}%"></i>` +
          `<i class="learn" style="width:${r.pctLearning}%"></i>` +
          `<i class="new" style="width:${r.pctNew}%"></i>` +
        "</div>" +
        sub +
      "</div>"
    );
  };
  return (
    '<div class="card">' +
      `<p class="h">Из чего словарь · ${num(total)} ${plural(total, "слово", "слова", "слов")}</p>` +
      '<p class="hsub">Часть речи берётся из базы знаний о языке, а не из очереди: ' +
      '<i>water</i> — существительное, даже когда встречается глаголом.</p>' +
      rows.map(line).join("") +
      '<div class="leg">' +
        '<span><i style="background:var(--accent)"></i>Выучено</span>' +
        '<span><i style="background:var(--accent-2)"></i>Учу</span>' +
        '<span><i style="background:color-mix(in srgb,var(--accent-2) 34%, var(--track))"></i>Впереди</span>' +
      "</div>" +
      '<p class="footnote in">«Выучено» — слово ушло на повтор через 30 дней ' +
      "(верхняя ступень планировщика).</p>" +
    "</div>"
  );
}

function ladderBlock(views: RungView[], hereFam: [number, number], ceilFam: [number, number]): string {
  const rung = (v: RungView) => {
    // Отметки могут прийтись на ОДНУ ступень (если словарь почти весь выучен) —
    // тогда показываем обе, а не молча теряем потолок.
    const tags =
      (v.here ? `<span class="youhere">вы здесь ≈ ${famRange(hereFam)}</span>` : "") +
      (v.ceiling ? `<span class="cap-tag">потолок ≈ ${famRange(ceilFam)}</span>` : "");
    const tag = tags ? `<span class="tags">${tags}</span>` : "";
    const cls = "rung" + (v.here ? " here" : "") + (v.passed ? " passed" : "");
    return (
      `<div class="${cls}">` +
        `<span class="fam">${esc(v.rung.label)}</span>` +
        `<div class="info"><div class="t">${esc(v.rung.what)}</div>` +
        `<div class="s">${esc(v.rung.src)}</div></div>` +
        tag +
      "</div>"
    );
  };
  return (
    '<div class="card">' +
      '<p class="h">Сколько слов — до свободного общения</p>' +
      // ⭐ Решение 29.07, доведённое до этого экрана 31.07: слово «семьи» с витрины
      // убрано — в разговоре его никто не употребляет, и плашка читалась как чужой
      // термин. Считаем по-прежнему СЕМЬЯМИ слов (это ядро, `progress.py`, оно не
      // тронуто) — просто говорим об этом человеческим языком, примером. Формулировка
      // взята та же, что в кабинете, чтобы экраны не спорили друг с другом.
      '<p class="hsub"><i>go · goes · went</i> — считаем за одно слово. ' +
      "У каждой ступени указан источник — это проверяемая шкала, а не «на глазок».</p>" +
      '<div class="ladder">' + views.map(rung).join("") + "</div>" +
    "</div>"
  );
}

function scaleBlock(bars: ScaleBar[], nextName: string, next: { verb: number; noun: number } | null): string {
  if (!bars.length) return "";
  const bar = (b: ScaleBar) =>
    '<div class="scaleitem">' +
      `<div class="top"><span class="nm">${esc(b.label)}</span>` +
      `<span class="ct">${num(b.value)} / ${num(b.target)}</span></div>` +
      `<div class="goalbar"><i style="width:${b.pct}%"></i></div>` +
    "</div>";
  const tail = next
    ? `<p class="footnote in">Следующая отметка — «${esc(nextName)}»: ` +
      `${num(next.verb)} глаголов и ${num(next.noun)} существительных.</p>`
    : "";
  return (
    '<div class="card">' +
      '<p class="h">Народный ориентир</p>' +
      '<p class="hsub">Твоя собственная отметка «базового» уровня. ⚠ Это ориентир, ' +
      "а не научная норма: доказательной нормы по частям речи не существует.</p>" +
      bars.map(bar).join("") +
      tail +
    "</div>"
  );
}

export function renderStats(host: HTMLElement): void {
  const p = state.progress;
  if (!p) { host.innerHTML = '<div class="stub"><p>Цифры ещё едут…</p></div>'; return; }

  const v = p.vocabulary;
  const ratio = p.family_ratio ?? [0.85, 0.87];
  const rows = posRows(v.by_pos || {});
  const views = ladderView(p.ladder || [], familyMid(v.learned, ratio), familyMid(v.total, ratio));
  const bars = scaleBars(p.owner_scale || [], v.verbs_learned, v.nouns_learned);
  const nextStep = (p.owner_scale || [])[1] ?? null;

  host.innerHTML =
    '<div class="two-col">' +
      '<div class="col-main">' +
        '<div class="greet"><h2>Твой словарь</h2>' +
        `<span class="streak">🌱 ${num(v.learned)} из ${num(v.total)}</span></div>` +
        '<div class="twin">' +
          '<div class="stat"><div class="k"><span class="dot" style="background:var(--accent-2)"></span>Узнаю</div>' +
            `<div class="v">${num(v.recognise)}</div>` +
            '<div class="cap">вижу английское — помню перевод</div></div>' +
          '<div class="stat"><div class="k"><span class="dot" style="background:var(--accent)"></span>Достаю</div>' +
            `<div class="v">${num(v.produce)}</div>` +
            '<div class="cap">сам перевожу на английский</div></div>' +
        "</div>" +
        '<p class="footnote">Одна цифра здесь солгала бы: узнавание и продукция — разные ' +
        "умения, и растут они порознь. Слово переходит в «достаю» на третьей ступени " +
        `памяти (бокс ${p.production_box}) — с неё занятие спрашивает его по-русски.</p>` +
        posBlock(rows, v.total) +
      "</div>" +
      '<div class="col-side">' +
        ladderBlock(views, p.you_are_here.families, p.ceiling.families) +
        scaleBlock(bars, nextStep?.name ?? "", nextStep) +
        '<p class="footnote">Уровень CEFR (A1…C2) не показываем: метки в базе проставила ' +
        "модель, а не валидированный профиль, — для вердикта об уровне они не годятся. " +
        `Отложенные наборы (${num(v.deferred)}) в шкалу входят, но в занятие не идут.</p>` +
      "</div>" +
    "</div>";
}
